import React, { useEffect, useState } from 'react';
import {
    Drawer,
    Checkbox,
    Button,
    Space,
    message,
    Typography,
    Divider,
    Empty,
    Spin,
    Card,
    Tag
} from 'antd';
import { SafetyCertificateOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { roleService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Text, Title } = Typography;

const PRIMARY_RED = '#DA291C';

const RolePermissionsDrawer = ({ visible, onClose, roleUuid, roleName, onSuccess }) => {
    const { permissions: currentUserPermissions } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [initialPermissions, setInitialPermissions] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState([]);

    useEffect(() => {
        if (visible && roleUuid) {
            fetchData();
        }
    }, [visible, roleUuid]);

    const fetchData = async () => {
        setFetching(true);
        try {
            // 1. Fetch available permissions (Full list if admin, or user's list)
            let allPerms = [];

            // Check if user has management permission
            if (currentUserPermissions.includes('system.roles.manage')) {
                const resPerms = await roleService.getAllPermissions();
                if (resPerms.status === 'success') {
                    allPerms = resPerms.data;
                }
            }

            // If we couldn't fetch all (or user restricted), fallback to own permissions (legacy mode)
            if (allPerms.length === 0) {
                allPerms = currentUserPermissions.map(slug => ({
                    slug,
                    name: slug,
                    module: slug.split('.')[0]
                }));
            }

            setAvailablePermissions(allPerms);

            // 2. Fetch Role Details
            const res = await roleService.getDetail(roleUuid);
            if (res.status === 'success') {
                const perms = res.data.direct_permissions || [];
                setSelectedPermissions(perms);
                setInitialPermissions(perms);
            }
        } catch (error) {
            message.error('Error al cargar datos');
            console.error(error);
        } finally {
            setFetching(false);
        }
    };

    const handlePermissionChange = (slug) => {
        setSelectedPermissions(prev =>
            prev.includes(slug)
                ? prev.filter(p => p !== slug)
                : [...prev, slug]
        );
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const res = await roleService.syncPermissions(roleUuid, selectedPermissions);
            if (res.status === 'success') {
                message.success('Permisos actualizados correctamente');
                onSuccess();
                onClose();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Error al guardar permisos');
        } finally {
            setLoading(false);
        }
    };

    const hasChanges = JSON.stringify([...selectedPermissions].sort()) !== JSON.stringify([...initialPermissions].sort());

    // Group permissions by module
    const groupedPermissions = availablePermissions.reduce((acc, perm) => {
        const group = perm.module || 'Otros';
        if (!acc[group]) acc[group] = [];
        acc[group].push(perm);
        return acc;
    }, {});

    return (
        <Drawer
            title={
                <Space>
                    <SafetyCertificateOutlined style={{ color: PRIMARY_RED }} />
                    <span>Permisos de: <Text strong>{roleName}</Text></span>
                </Space>
            }
            width={600}
            onClose={onClose}
            open={visible}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        loading={loading}
                        disabled={!hasChanges}
                        style={{ background: PRIMARY_RED, borderColor: PRIMARY_RED }}
                    >
                        Guardar Cambios
                    </Button>
                </Space>
            }
        >
            <div style={{ marginBottom: 24 }}>
                <Card size="small" style={{ background: '#f5f5f5', border: 'none' }}>
                    <Space align="start">
                        <InfoCircleOutlined style={{ color: '#1890ff', marginTop: 4 }} />
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Seleccione los permisos que tendrá este rol.
                        </Text>
                    </Space>
                </Card>
            </div>

            {fetching ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin tip="Cargando permisos..." />
                </div>
            ) : Object.keys(groupedPermissions).length > 0 ? (
                Object.keys(groupedPermissions).map(group => (
                    <div key={group} style={{ marginBottom: 24 }}>
                        <Title level={5} style={{ textTransform: 'capitalize', color: '#555', marginBottom: 12 }}>
                            {group}
                        </Title>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {groupedPermissions[group].map(perm => (
                                <div
                                    key={perm.slug}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: 6,
                                        background: selectedPermissions.includes(perm.slug) ? '#fff1f0' : 'transparent',
                                        transition: 'all 0.3s',
                                        border: '1px solid',
                                        borderColor: selectedPermissions.includes(perm.slug) ? '#ffa39e' : '#f0f0f0'
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedPermissions.includes(perm.slug)}
                                        onChange={() => handlePermissionChange(perm.slug)}
                                        style={{ width: '100%' }}
                                    >
                                        <Space direction="vertical" size={0}>
                                            <Text strong={selectedPermissions.includes(perm.slug)}>{perm.name || perm.slug}</Text>
                                            {perm.description && (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>{perm.description}</Text>
                                            )}
                                        </Space>
                                    </Checkbox>
                                </div>
                            ))}
                        </Space>
                        <Divider style={{ margin: '16px 0' }} />
                    </div>
                ))
            ) : (
                <Empty description="No tienes permisos para asignar" />
            )}
        </Drawer>
    );
};

export default RolePermissionsDrawer;
